const root = document.getElementById('canvasMount');
import { configureAudio, getAudioBuses, getAudioContext, playImpact, playWeaponShot, setListenerPosition, stopMusic, unlockAudio } from './audio/audio.js';
import { initMusic, triggerStinger, updateMusic } from './audio/music.js';
import { playLevelUpCue, updateSfx } from './audio/sfx.js';
import { adjustCameraZoom, initCamera, setCameraFov, updateCameraFollow } from './engine/camera.js';
import { initLoaders, loadAssets, loadWeaponAsset } from './engine/loaders.js';
import { startLoop } from './engine/loop.js';
import { initPostprocessing, renderPostprocessing } from './engine/postprocessing.js';
import { applyQualityPreset, detectGpuTier } from './engine/quality.js';
import { initRenderer } from './engine/renderer.js';
import { initScene } from './engine/scene.js';
import { initBullets, spawnBullet, updateBullets } from './entities/bullets.js';
import { initBossDirector, updateBossDirector } from './entities/boss.js';
import { initDrones, ensureDrones, updateDrones } from './entities/drones.js';
import { initEnemies, spawnEnemy, updateEnemies } from './entities/enemies.js';
import { applyHeroModel } from './entities/enemy_models.js';
import { initEnemyShots, spawnEnemyShot, updateEnemyShots } from './entities/enemy_shots.js';
import { dashPlayer, initPlayer, updatePlayer, updatePlayerMaterials } from './entities/player.js';
import { initPlayerAnim, updatePlayerAnim } from './entities/player_anim.js';
import { handleBulletExpire, handleBulletHit, handleEnemyContact, handleEnemyShotHit, initCombat, updatePowers } from './game/combat.js';
import { initInput, pollGamepad, rumbleGamepad } from './game/input.js';
import { initPointerAim } from './game/pointer.js';
import { applyUpgrade, createUpgradeChoices } from './game/progression.js';
import { initRunControl, showGameOverIfNeeded } from './game/run_control.js';
import { game, resetGame } from './game/state.js';
import { initWaveDirector, updateWaveDirector } from './game/waves.js';
import { initWavedash, reportWavedashProgress, toggleWavedashOverlay } from './platform/wavedash.js';
import { initDamageNumbers, spawnDamageNumber, updateDamageNumbers } from './ui/damage_numbers.js';
import { showToast } from './ui/toast.js';
import { initHud, pushKillFeed, updateHud } from './ui/hud.js';
import { initLeaderboard } from './ui/leaderboard.js';
import { hideLobby, initLobby } from './ui/lobby.js';
import { initPolish } from './ui/polish.js';
import { initUpgrades, showUpgradeScreen } from './ui/upgrades.js';
import { hideWeaponWheel, initWeaponWheel, showWeaponWheel } from './ui/weapon_wheel.js';
import { initDecals, spawnDecal, updateDecals } from './vfx/decals.js';
import { initBeams, spawnBeam, spawnExplosionRing, spawnLightningArc, updateBeams } from './vfx/beams.js';
import { initParticles, setParticleQuality, spawn, spawnMuzzleFlash, spawnParticles, spawnShellCasing, updateParticles } from './vfx/particles.js';
import { flashScreen, initScreenFx, setFlashReduction, updateDamageFx } from './vfx/screenfx.js';
import { initTracers, spawnTracer, updateTracers } from './vfx/tracers.js';
import { cycleWeapon, fireWeapon, currentWeapon, getWeaponSlots, initFiring, selectWeapon, unlockWeapon, updateFiring } from './weapons/firing.js';
import { applyRecoil, initViewmodel, setADS, setViewmodelWeapon, updateViewmodel } from './weapons/viewmodel.js';
import { initGround } from './world/ground.js';
import { initLighting, setSunShadows, updateDayNightCycle } from './world/lighting.js';
import { initHeroFlashlight, updateHeroFlashlight } from './world/flashlight.js';
import { initGeneratedMap } from './world/map.js';
import { initObstacles } from './world/obstacles.js';
import { initWeather, updateWeather } from './world/weather.js';
const renderer = root ? initRenderer({
  mount: root,
  pixelRatio: Math.min(window.devicePixelRatio || 1, 0.85),
}) : null;
const { scene, world } = initScene();
const camera = initCamera({ fov: 62 });
const getPointerWorld = initPointerAim(camera, renderer?.domElement);
if (renderer) initPostprocessing(renderer, scene, camera, {
  bloomEnabled: true,
  bloomStrength: 1.1,
  bloomThreshold: 0.5,
  initialPixelRatio: Math.min(window.devicePixelRatio || 1, 0.85),
});
initLoaders({ renderer });
boot();
async function boot() {
  await loadAssets(reportWavedashProgress);
  initWavedash();
  let quality = renderer ? applyQualityPreset(detectGpuTier(renderer), { world }) : null;
  initLighting(scene, {
    shadows: !!quality?.shadows,
    shadowMapSize: quality?.shadowMapSize || 1024,
  });
  initGround(scene, world);
  initObstacles(world);
  const player = initPlayer(scene, { visible: true });
  applyHeroModel(player);
  initHeroFlashlight(scene, player);
  resetGame({ mode: 'lobby' });
  initGeneratedMap(world, { game });
  const enemySystem = initEnemies(world);
  initBullets(world);
  initEnemyShots(world);
  initDrones(world);
  ensureDrones(1, { quality: quality?.tier || 'low' });
  initBossDirector(world);
  initWaveDirector(game);
  initParticles(world, { quality: quality?.tier || 'low' });
  initBeams(world);
  initTracers(world);
  initDecals(world);
  initWeather(world);
  initPlayerAnim(player.group);
  initScreenFx();
  initViewmodel(renderer, scene, camera);
  initHud();
  initDamageNumbers();
  initLeaderboard();
  initCombat({
    camera,
    currentWeapon,
    getEnemies: () => enemySystem.enemies,
    flashScreen,
    playImpact,
    pushKillFeed,
    rumbleGamepad,
    spawn,
    spawnDamageNumber,
    spawnDecal,
    spawnBeam,
    spawnExplosionRing,
    spawnLightningArc,
    spawnParticles,
    updateDamageFx,
  });
  initPolish();
  initUpgrades();
  initWeaponWheel({
    getSlots: getWeaponSlots,
    onSelect: (index) => selectWeapon(index),
    onSlowmoChange: (scale) => { game.timeScale = scale; },
  });
  initLobby({
    onStart: () => { game.mode = 'playing'; showToast('Wave 1', 1500); },
    onLobby: () => { game.mode = 'lobby'; },
    onPause: togglePause,
    onQualityChange: (tier) => {
      if (!renderer) return;
      quality = applyQualityPreset(tier, { world });
      setSunShadows(quality.shadows, quality.shadowMapSize);
      setParticleQuality(quality.tier);
    },
    onVolumeChange: (volume) => configureAudio({ volume }),
    onShakeChange: (enabled) => { game.shakeEnabled = enabled; },
    onShakeIntensityChange: (amount) => { game.shakeIntensity = amount; },
    onFovChange: (fov) => setCameraFov(fov),
    onFlashReductionChange: (enabled) => setFlashReduction(enabled),
    onHeroChange: (id) => applyHeroModel(player, id),
    onColorsChange: (colors) => updatePlayerMaterials(colors),
  });
  initRunControl({ player, game });
  const playerInput = initInput({
    onDash: () => dashPlayer({ input: playerInput, game }),
    onFire: () => fireWeapon(player, game),
    onStart: () => hideLobby(),
    onTogglePause: togglePause,
    onPlatformOverlay: () => toggleWavedashOverlay(),
    onSelectWeapon: (index) => selectWeapon(index),
    onCycleWeapon: (direction) => cycleWeapon(direction),
    onWeaponWheelShow: () => showWeaponWheel(),
    onWeaponWheelHide: () => hideWeaponWheel(),
    onCameraZoom: (direction) => adjustCameraZoom(direction),
    onADS: (active) => {
      game.ads = active;
      setADS(active);
    },
  });
  initFiring({
    spawnBullet,
    onFired: (weapon, bullets) => {
      playWeaponShot(weapon, player.pos, player.pos);
      applyRecoil(weapon.shake || 0.08);
      spawnShellCasing(player, weapon);
      for (const bullet of bullets) spawnTracer(bullet.pos, bullet.vel.clone().normalize(), { color: weapon.color });
    },
    onWeaponChanged: (weapon) => loadWeaponModel(weapon),
    onWeaponUnlocked: (weapon) => {
      if (weapon.id === currentWeapon().id) loadWeaponModel(weapon);
    },
    onMuzzleFlash: (activePlayer, weapon) => {
      spawnMuzzleFlash(activePlayer, weapon);
      flashScreen('rgba(83,255,181,0.58)', weapon.id === 'rail' ? 0.13 : 0.06, 50);
    },
  });
  loadWeaponModel(currentWeapon());
  unlockWeapon(1);
  unlockWeapon(2);
  unlockWeapon(3);
  startLoop({
    update: (dt) => {
      const simDt = dt * (game.timeScale || 1);
      game.elapsed += simDt;
      if (game.mode !== 'playing') {
        showGameOverIfNeeded();
        updateRuntimeHud(enemySystem);
        return;
      }
      pollGamepad({
        onDash: () => dashPlayer({ input: playerInput, game }),
        onFire: () => fireWeapon(player, game),
        onCycleWeapon: (direction) => cycleWeapon(direction),
        onStart: () => {
          hideLobby();
          game.mode = 'playing';
        },
      });
      setListenerPosition(player.pos);
      updateFiring(simDt, player, game);
      updatePlayer(simDt, { input: playerInput, game, world, pointerWorld: getPointerWorld() });
      if (playerInput.shooting) fireWeapon(player, game);
      updatePlayerAnim(player.vel, true, simDt);
      updateHeroFlashlight(simDt);
      updateDayNightCycle(scene, game.elapsed);
      updateCameraFollow(simDt, player, { shakeEnabled: game.shakeEnabled, shakeScale: game.shakeIntensity, cameraHeight: 24, cameraDistance: 20 });
      updateViewmodel(simDt, player);
      updateBullets(simDt, {
        enemies: enemySystem.enemies,
        onHit: (enemy, bullet) => handleBulletHit(enemy, bullet),
        onExpire: (bullet) => handleBulletExpire(bullet),
      });
      updateDrones(simDt, {
        player,
        enemies: enemySystem.enemies,
        elapsed: game.elapsed,
        damageMultiplier: 1 + (game.upgrades.orbitals || 0) * 0.18,
        onHit: (enemy, damage) => handleBulletHit(enemy, { damage, color: 0x53ffb5, pos: enemy.pos, hits: new Set(), pierce: 0 }),
      });
      updateWaveDirector(simDt, {
        player,
        enemies: enemySystem.enemies,
        spawnEnemy,
        onWaveStart: (wave) => {
          showToast(`Wave ${wave}`);
          if (wave === 1) triggerStinger('combat');
        },
        onBossWave: (wave) => {
          showToast(`Boss wave ${wave}`);
          triggerStinger('boss');
        },
      });
      updateBossDirector(simDt, { player });
      updateEnemies(simDt, {
        player,
        game,
        onContact: (enemy, damage) => handleEnemyContact(enemy, damage, player),
        onRangedAttack: (enemy) => spawnEnemyShot(enemy, player, game),
      });
      updateEnemyShots(simDt, { player, onHit: handleEnemyShotHit });
      ensureDrones(1 + Math.min(5, game.upgrades.orbitals || 0), { quality: quality?.tier || 'low' });
      updatePowers(simDt, { player, enemies: enemySystem.enemies });
      updateParticles(simDt);
      updateBeams(simDt);
      updateDamageNumbers(dt, camera);
      updateTracers(simDt);
      updateDecals(simDt);
      updateWeather(simDt, { playerPosition: player.pos, quality: quality?.tier || 'low' });
      updateMusic(threatLevel(enemySystem.enemies, game), simDt, { bossActive: !!game.boss?.alive });
      updateSfx(simDt, { player, game, world });
      showPendingUpgrade();
      updateRuntimeHud(enemySystem);
    },
    render: () => (renderer ? renderPostprocessing() : null),
  });
  window.addEventListener('pointerdown', unlockOnce, { once: false });
  window.addEventListener('keydown', unlockOnce, { once: false });
}
function togglePause() {
  game.mode = game.mode === 'paused' ? 'playing' : 'paused';
}
function showPendingUpgrade() {
  if (!game.pendingUpgrade) return;
  game.pendingUpgrade = false;
  game.mode = 'upgrade';
  playLevelUpCue();
  showUpgradeScreen(createUpgradeChoices(4), {
    state: game,
    onPick: (upgrade) => {
      applyUpgrade(game, upgrade);
      showToast(`Upgrade installed: ${upgrade.name}`);
      game.mode = 'playing';
    },
    onReroll: () => createUpgradeChoices(4),
    onSkip: () => { game.mode = 'playing'; },
  });
}
function updateRuntimeHud(enemySystem) {
  updateHud(game, {
    enemies: enemySystem.enemies,
    weapon: currentWeapon(),
    boss: game.boss,
  });
}
function loadWeaponModel(weapon) {
  return loadWeaponAsset(weapon.id).then((asset) => {
    if (asset) setViewmodelWeapon(asset.scene || asset, weapon);
  });
}
function threatLevel(enemies, state) {
  const enemyThreat = Math.min(1, enemies.length / 34);
  const bossThreat = state.boss?.alive ? 1 : 0;
  const lowHealth = state.health < state.maxHealth * 0.3 ? 0.25 : 0;
  return Math.min(1, enemyThreat * 0.85 + bossThreat + lowHealth);
}
function unlockOnce() {
  unlockAudio();
  stopMusic();
  initMusic(getAudioContext(), getAudioBuses().musicBus);
  showToast('Audio engine ready');
  window.removeEventListener('pointerdown', unlockOnce);
  window.removeEventListener('keydown', unlockOnce);
}
